import React, { useState, useEffect, useRef, useCallback } from 'react';
import './game.css'; // Import the new, improved CSS

// A more common and extensive dictionary for a professional game.
const COMMON_DICTIONARY = [
    'apple', 'banana', 'orange', 'grape', 'lemon', 'berry', 'melon', 'peach', 'plum', 'kiwi',
    'house', 'garden', 'flower', 'tree', 'river', 'mountain', 'cloud', 'sky', 'earth', 'ocean',
    'read', 'write', 'learn', 'teach', 'study', 'school', 'book', 'paper', 'pen', 'desk',
    'happy', 'smile', 'laugh', 'peace', 'calm', 'bright', 'shine', 'glow', 'warm', 'gentle',
    'program', 'code', 'data', 'logic', 'system', 'build', 'create', 'design', 'solution', 'project',
    'game', 'play', 'level', 'score', 'start', 'end', 'win', 'lose', 'challenge', 'progress',
    'friend', 'family', 'people', 'world', 'human', 'nature', 'animal', 'plant', 'food', 'water',
    'table', 'chair', 'lamp', 'door', 'window', 'floor', 'wall', 'roof', 'home', 'room',
    // Example words for target word generation (can be more extensive)
    'EDUCATION', 'DEVELOPMENT', 'COMMUNITY', 'HARMONY', 'ADVENTURE', 'EXPLORATION', 'INNOVATION',
    'EXCELLENCE', 'KNOWLEDGE', 'CHALLENGE', 'BRAIN', 'PUZZLE', 'WIZARD', 'MEMORY', 'MATCH'
];

const DIFFICULTY_SETTINGS = {
    easy: {
        wordLength: { min: 6, max: 8 },
        time: 120, // More time
        message: "A gentle start to sharpen your word skills.",
        potentialWords: ['CHALLENGE', 'BRAIN', 'PUZZLE', 'WIZARD', 'MEMORY'] // Examples
    },
    medium: {
        wordLength: { min: 9, max: 10 },
        time: 90, // Standard time
        message: "The perfect balance of challenge and fun.",
        potentialWords: ['KNOWLEDGE', 'COMMUNITY', 'DEVELOPMENT', 'HARMONY'] // Examples
    },
    hard: {
        wordLength: { min: 11, max: 12 },
        time: 60, // Less time
        message: "For true word masters seeking the ultimate test!",
        potentialWords: ['EXPLORATION', 'INNOVATION', 'EXCELLENCE', 'ADVENTURE', 'EDUCATION'] // Examples
    }
};

const WordWizard = () => {
    const [scrambledLetters, setScrambledLetters] = useState([]);
    const [currentInput, setCurrentInput] = useState('');
    const [foundWords, setFoundWords] = useState(new Set());
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(0);
    const [gameStatus, setGameStatus] = useState('menu'); // 'menu', 'playing', 'gameOver'
    const [difficulty, setDifficulty] = useState('medium'); // Default difficulty
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success', 'warning', 'error'

    const timerRef = useRef(null);
    const inputRef = useRef(null);

    const showMessage = useCallback((msg, type = '') => {
        setMessage(msg);
        setMessageType(type);
        const timeout = setTimeout(() => {
            setMessage('');
            setMessageType('');
        }, 2500); // Message visible for 2.5 seconds
        return () => clearTimeout(timeout);
    }, []);

    const shuffleArray = useCallback((array) => {
        let currentIndex = array.length, randomIndex;
        while (currentIndex !== 0) {
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex--;
            [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
        }
        return array;
    }, []);

    const chooseTargetWord = useCallback((difficultyLevel) => {
        const words = DIFFICULTY_SETTINGS[difficultyLevel].potentialWords;
        const randomWord = words[Math.floor(Math.random() * words.length)];
        return randomWord;
    }, []);

    const startGame = useCallback(() => {
        const targetWord = chooseTargetWord(difficulty);
        const letters = shuffleArray(targetWord.toUpperCase().split(''));

        setScrambledLetters(letters);
        setCurrentInput('');
        setFoundWords(new Set());
        setScore(0);
        setTimeLeft(DIFFICULTY_SETTINGS[difficulty].time);
        setGameStatus('playing');
        setMessage('');
        setMessageType('');

        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setTimeLeft(prevTime => {
                if (prevTime <= 1) {
                    clearInterval(timerRef.current);
                    setGameStatus('gameOver');
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);

        setTimeout(() => { // Give a small delay for UI to render and focus
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 100);
    }, [difficulty, chooseTargetWord, shuffleArray]);

    const handleLetterClick = (letter) => {
        // Create a frequency map of available letters
        const availableLettersMap = scrambledLetters.reduce((acc, char) => {
            acc[char] = (acc[char] || 0) + 1;
            return acc;
        }, {});

        // Create a frequency map of letters in currentInput
        const currentInputMap = currentInput.split('').reduce((acc, char) => {
            acc[char] = (acc[char] || 0) + 1;
            return acc;
        }, {});

        // Check if adding the new letter exceeds available count
        if (currentInputMap[letter] && currentInputMap[letter] >= availableLettersMap[letter]) {
            showMessage(`All instances of '${letter}' are already used!`, 'warning');
            return;
        }

        setCurrentInput(prevInput => prevInput + letter);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleClearInput = () => {
        setCurrentInput('');
        showMessage(''); // Clear any previous messages
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value.toUpperCase();
        const availableLetters = [...scrambledLetters];
        let cleanedValue = '';
        let isValid = true;
        let tempCharCounts = {}; // Track character counts for current input

        for (let char of value) {
            tempCharCounts[char] = (tempCharCounts[char] || 0) + 1;

            // Check if this character is available in scrambled letters and not overused
            const availableCount = availableLetters.filter(l => l === char).length;
            if (tempCharCounts[char] > availableCount) {
                isValid = false; // Overused a letter
                break;
            }
            cleanedValue += char;
        }

        if (!isValid && value.length > 0) {
            showMessage('Invalid letter or already used!', 'warning');
        } else if (messageType !== 'success') { // Don't clear success messages prematurely
            showMessage('');
        }

        setCurrentInput(cleanedValue);
    };


    const handleSubmitWord = (e) => {
        e.preventDefault();
        const word = currentInput.toLowerCase();

        if (word.length < 3) {
            showMessage('Word too short! (min 3 letters)', 'warning');
            return;
        }

        if (!COMMON_DICTIONARY.includes(word)) {
            showMessage("Not a valid English word!", 'error');
            return;
        }

        if (foundWords.has(word)) {
            showMessage("Word already found!", 'warning');
            return;
        }

        // Final robust check: ensure the word can be formed from available letters
        const scrambledLetterCounts = scrambledLetters.reduce((acc, char) => {
            acc[char] = (acc[char] || 0) + 1;
            return acc;
        }, {});

        const inputLetterCounts = word.toUpperCase().split('').reduce((acc, char) => {
            acc[char] = (acc[char] || 0) + 1;
            return acc;
        }, {});

        let canForm = true;
        for (let char in inputLetterCounts) {
            if (inputLetterCounts[char] > (scrambledLetterCounts[char] || 0)) {
                canForm = false;
                break;
            }
        }

        if (!canForm) {
            showMessage("Cannot form this word from available letters!", 'error');
            return;
        }

        setFoundWords(prevWords => new Set(prevWords).add(word));
        const points = word.length * 10;
        setScore(prevScore => prevScore + points);
        setCurrentInput('');
        showMessage(`Word accepted! +${points} points`, 'success');
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    const renderGameMenu = () => (
        <div className="game-menu-screen">
            <h2 className="menu-title">Choose Your Challenge</h2>
            <div className="difficulty-options">
                {Object.keys(DIFFICULTY_SETTINGS).map(level => (
                    <button
                        key={level}
                        className={`difficulty-button ${difficulty === level ? 'active' : ''}`}
                        onClick={() => setDifficulty(level)}
                    >
                        {level.toUpperCase()}
                    </button>
                ))}
            </div>
            <p className="difficulty-description">
                {DIFFICULTY_SETTINGS[difficulty].message}
            </p>
            <button className="primary-button start-button" onClick={startGame}>Start Game</button>
        </div>
    );

    const renderGamePlaying = () => (
        <div className="game-active-area">
            <div className="game-header-stats">
                <div className="stat-box timer-box">
                    <span className="stat-label">TIME</span>
                    <span className={`stat-value ${timeLeft <= 10 ? 'time-critical' : ''}`}>{timeLeft}s</span>
                </div>
                <div className="stat-box score-box">
                    <span className="stat-label">SCORE</span>
                    <span className="stat-value">{score}</span>
                </div>
            </div>

            <div className="scrambled-letters-tray">
                {scrambledLetters.map((letter, index) => {
                    const currentInputLetterCount = currentInput.split('').filter(c => c === letter).length;
                    const scrambledLetterCount = scrambledLetters.filter(l => l === letter).length;
                    const isUsed = currentInputLetterCount >= scrambledLetterCount;

                    return (
                        <button
                            key={index} // Use index as key here, as letters can repeat and their positions are unique
                            className={`letter-tile ${isUsed ? 'used' : ''}`}
                            onClick={() => handleLetterClick(letter)}
                            disabled={isUsed}
                        >
                            {letter}
                        </button>
                    );
                })}
            </div>

            <form onSubmit={handleSubmitWord} className="word-input-controls">
                <input
                    ref={inputRef}
                    type="text"
                    value={currentInput}
                    onChange={handleInputChange}
                    maxLength={scrambledLetters.length}
                    placeholder="TYPE YOUR WORD HERE"
                    className="word-input-field"
                    autoComplete="off"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck="false"
                />
                <div className="input-action-buttons">
                    <button type="submit" className="action-button submit-button">SUBMIT</button>
                    <button type="button" className="action-button clear-button" onClick={handleClearInput}>CLEAR</button>
                </div>
            </form>

            {message && <p className={`game-feedback-message ${messageType}`}>{message}</p>}

            <div className="found-words-display">
                <h3 className="found-words-title">Found Words ({foundWords.size}):</h3>
                <ul className="found-words-list-scroll">
                    {[...foundWords].sort().map((word, index) => (
                        <li key={index} className="found-word-item">{word}</li>
                    ))}
                </ul>
            </div>
        </div>
    );

    const renderGameOver = () => (
        <div className="game-over-screen">
            <h2 className="game-over-title">GAME OVER</h2>
            <p className="final-summary">Your final score: <span className="final-score-value">{score}</span></p>
            <p className="final-summary">You found <span className="final-words-count-value">{foundWords.size}</span> words.</p>
            <button className="primary-button play-again-button" onClick={() => setGameStatus('menu')}>PLAY AGAIN</button>
        </div>
    );

    return (
        <div className="word-wizard-game-container">
            <h1 className="game-main-title">WORD WIZARD</h1>
            <div className="game-board-panel">
                {gameStatus === 'menu' && renderGameMenu()}
                {gameStatus === 'playing' && renderGamePlaying()}
                {gameStatus === 'gameOver' && renderGameOver()}
            </div>
        </div>
    );
};

export default WordWizard;